import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
    Loader2, Trophy, ChevronRight, RotateCcw, Lock, ArrowLeft,
    Check, X, Flame,
} from 'lucide-react';
import useSEO from '@/hooks/useSEO';

/**
 * /countries/:slug/quiz — Gamified quiz play surface.
 *
 * Single-question-at-a-time loop:
 *   GET /quiz/next → render → user picks answer → POST /quiz/answer → reveal
 *   correct + explanation → "Next question" (paid) OR "Come back tomorrow" (free)
 *
 * Visual feedback (no sound):
 *   Correct → big green checkmark overlay, green pulse on the option, green
 *             border + "+pts" popup.
 *   Wrong   → big red X overlay, red shake on the option, the correct option
 *             highlighted in green so the child still learns.
 *   Typed answer → letter-by-letter colouring (green = matched any accepted
 *             answer prefix, red = mismatched character) plus the standard
 *             reveal card.
 */
export default function CountryQuiz() {
    const { slug } = useParams();
    const { user, activeChildId } = useAuth();
    const navigate = useNavigate();
    const [state, setState] = useState('loading'); // loading | playing | reveal | locked | error | empty
    const [question, setQuestion] = useState(null);
    const [meta, setMeta] = useState({ paid: false, points_per_correct: 2 });
    const [answer, setAnswer] = useState({ idx: null, typed: '' });
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [streak, setStreak] = useState(0);
    const [comboFlash, setComboFlash] = useState(false);
    const [overlay, setOverlay] = useState(null); // 'correct' | 'wrong' | null
    const overlayTimer = useRef(null);
    const comboTimer = useRef(null);

    useSEO({
        title: `Country quiz | Linguistic Quest`,
        description: 'Country quiz — earn points for your family on the leaderboard.',
        path: `/countries/${slug}/quiz`,
    });

    const fetchNext = useCallback(async () => {
        setState('loading'); setAnswer({ idx: null, typed: '' }); setResult(null); setOverlay(null);
        try {
            const params = new URLSearchParams();
            if (activeChildId) params.set('child_profile_id', activeChildId);
            const { data } = await api.get(`/countries/${slug}/quiz/next${params.toString() ? `?${params.toString()}` : ''}`);
            setQuestion(data.question);
            setMeta({ paid: data.paid, points_per_correct: data.points_per_correct });
            setState('playing');
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail || 'Could not load question';
            if (status === 402) { setState('locked'); setError(detail); }
            else if (status === 404) { setState('empty'); setError(detail); }
            else { setState('error'); setError(detail); }
        }
    }, [slug, activeChildId]);

    useEffect(() => { fetchNext(); }, [fetchNext]);

    // Clean up overlay timer
    useEffect(() => () => {
        if (overlayTimer.current) clearTimeout(overlayTimer.current);
        if (comboTimer.current) clearTimeout(comboTimer.current);
    }, []);

    const triggerOverlay = (kind) => {
        setOverlay(kind);
        if (overlayTimer.current) clearTimeout(overlayTimer.current);
        overlayTimer.current = setTimeout(() => setOverlay(null), 900);
    };

    const submit = async () => {
        if (!question) return;
        const presentAsMC = question.question_type === 'multiple_choice' || question.display_as === 'multiple_choice';
        if (presentAsMC && answer.idx === null) {
            toast.error('Pick an answer first'); return;
        }
        if (!presentAsMC && !answer.typed.trim()) {
            toast.error('Type your answer first'); return;
        }
        try {
            // For native MC questions we send selected_option_index.
            // For typed-converted-to-MC (display_as='multiple_choice' but
            // question_type='typed'), we send the picked label as typed_answer
            // so the existing typed grading path handles it without any
            // server-side state.
            const isNativeMC = question.question_type === 'multiple_choice';
            const pickedLabel = presentAsMC && answer.idx !== null
                ? (question.options?.[answer.idx]?.label || '')
                : null;
            const body = {
                question_id: question.id,
                child_profile_id: activeChildId || null,
                ...(isNativeMC
                    ? { selected_option_index: answer.idx }
                    : presentAsMC
                        ? { typed_answer: pickedLabel }
                        : { typed_answer: answer.typed.trim() }),
            };
            const { data } = await api.post(`/countries/${slug}/quiz/answer`, body);
            setResult(data);
            setState('reveal');
            setStreak(data.streak_after ?? (data.is_correct ? streak + 1 : 0));
            triggerOverlay(data.is_correct ? 'correct' : 'wrong');
            if ((data.combo_multiplier || 1) > 1) {
                setComboFlash(true);
                if (comboTimer.current) clearTimeout(comboTimer.current);
                comboTimer.current = setTimeout(() => setComboFlash(false), 1600);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not submit answer');
        }
    };

    if (!user) {
        return (
            <main className="max-w-2xl mx-auto px-6 py-16 text-center">
                <p className="text-lq-ink-700">Please <Link to={`/login?next=/countries/${slug}/quiz`} className="text-lq-purple-700 underline">sign in</Link> to play.</p>
            </main>
        );
    }

    return (
        <main className="max-w-2xl mx-auto px-6 py-12 relative" data-testid="page-country-quiz">
            <Link to={`/countries/${slug}`} className="inline-flex items-center gap-1 text-sm text-lq-ink-500 hover:text-lq-purple-700"><ArrowLeft size={14}/> Back to country</Link>

            {/* Big animated feedback overlay (non-blocking, auto-dismisses) */}
            {overlay && (
                <div
                    className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center"
                    aria-live="polite"
                    aria-hidden={!overlay}
                    data-testid={`quiz-overlay-${overlay}`}
                >
                    <div className={`quiz-burst ${overlay === 'correct' ? 'quiz-burst-correct' : 'quiz-burst-wrong'}`}>
                        {overlay === 'correct' ? (
                            <Check strokeWidth={4} size={140} />
                        ) : (
                            <X strokeWidth={4} size={140} />
                        )}
                    </div>
                </div>
            )}

            {state === 'loading' && (
                <div className="mt-12 text-center text-lq-ink-500"><Loader2 className="inline-block animate-spin"/> Loading question…</div>
            )}

            {state === 'empty' && (
                <div className="mt-12 bg-white border border-lq-ink-100 rounded-2xl p-8 text-center" data-testid="quiz-empty">
                    <h1 className="font-display text-2xl font-semibold text-lq-ink-900">No questions yet</h1>
                    <p className="mt-2 text-lq-ink-500">{error || "We're still preparing the quiz for this country."}</p>
                    <Link to={`/countries/${slug}`} className="mt-5 inline-block px-5 py-2 rounded-full bg-lq-purple-500 text-white font-semibold">Browse facts instead</Link>
                </div>
            )}

            {state === 'locked' && (
                <div className="mt-12 bg-white border border-lq-ink-100 rounded-2xl p-8 text-center" data-testid="quiz-locked">
                    <Lock className="mx-auto text-lq-warn" size={36}/>
                    <h1 className="mt-3 font-display text-2xl font-semibold text-lq-ink-900">You've used today's free quiz</h1>
                    <p className="mt-2 text-lq-ink-500">{error || 'Subscribe for unlimited daily play.'}</p>
                    <div className="mt-5 flex justify-center gap-3 flex-wrap">
                        <Link to="/pricing" className="px-5 py-2 rounded-full bg-lq-purple-500 text-white font-semibold hover:bg-lq-purple-700" data-testid="quiz-upgrade">Subscribe →</Link>
                        <Link to={`/countries/${slug}`} className="px-5 py-2 rounded-full bg-white border border-lq-ink-300 text-lq-ink-900 hover:bg-lq-ink-100">Read facts</Link>
                    </div>
                </div>
            )}

            {state === 'error' && (
                <div className="mt-12 bg-white border border-lq-ink-100 rounded-2xl p-8 text-center text-lq-error">{error}</div>
            )}

            {(state === 'playing' || state === 'reveal') && question && (
                <article className="mt-8 bg-white border border-lq-ink-100 rounded-2xl p-6 md:p-8" data-testid="quiz-card">
                    <div className="flex items-center justify-between text-xs uppercase tracking-wider text-lq-ink-500">
                        <span className="inline-flex items-center gap-1"><Trophy size={12}/> {meta.points_per_correct} pts per correct · {question.difficulty}</span>
                        {streak > 0 && (
                            <span
                                className={`text-lq-peach-600 inline-flex items-center gap-1 ${comboFlash ? 'combo-flash' : ''}`}
                                data-testid="quiz-streak"
                            >
                                <Flame size={14} className="text-lq-peach-600" />
                                <span className="font-mono">{streak}</span>
                                <span>in a row</span>
                                {streak >= 3 && streak % 3 === 0 && (
                                    <span className="ml-1 px-1.5 py-0.5 rounded-md bg-lq-peach-500 text-white text-[10px] font-bold tracking-wider" data-testid="quiz-combo-badge">2× COMBO</span>
                                )}
                            </span>
                        )}
                    </div>
                    <h1 className="mt-3 font-display text-2xl text-lq-ink-900 leading-snug" data-testid="quiz-question">{question.question}</h1>

                    {(question.question_type === 'multiple_choice' || question.display_as === 'multiple_choice') ? (
                        <MultipleChoiceOptions
                            question={question}
                            answer={answer}
                            setAnswer={setAnswer}
                            result={result}
                            state={state}
                        />
                    ) : (
                        <TypedAnswerField
                            question={question}
                            answer={answer}
                            setAnswer={setAnswer}
                            result={result}
                            state={state}
                            onEnter={submit}
                        />
                    )}

                    {state === 'reveal' && result?.explanation && (
                        <div className="mt-5 p-4 rounded-xl bg-lq-purple-50 border border-lq-purple-100 text-sm text-lq-ink-900" data-testid="quiz-explanation">
                            <strong>Why:</strong> {result.explanation}
                        </div>
                    )}

                    <div className="mt-6 flex flex-wrap gap-3 items-center">
                        {state === 'playing' && (
                            <button onClick={submit} className="px-5 py-2 rounded-full bg-lq-purple-500 text-white font-semibold hover:bg-lq-purple-700" data-testid="quiz-submit">
                                Submit answer
                            </button>
                        )}
                        {state === 'reveal' && (
                            <>
                                <span className={`text-sm font-semibold ${result.is_correct ? 'text-lq-success' : 'text-lq-error'}`} data-testid="quiz-result">
                                    {result.is_correct
                                        ? ((result.combo_multiplier || 1) > 1
                                            ? `🔥 2× COMBO! +${result.points_awarded} pts to your family leaderboard`
                                            : `✓ +${result.points_awarded} pts to your family leaderboard`)
                                        : '✗ No points this time'}
                                </span>
                                {meta.paid ? (
                                    <button onClick={fetchNext} className="ml-auto px-5 py-2 rounded-full bg-lq-purple-500 text-white font-semibold hover:bg-lq-purple-700 inline-flex items-center gap-1" data-testid="quiz-next">
                                        Next question <ChevronRight size={14}/>
                                    </button>
                                ) : (
                                    <div className="ml-auto flex flex-wrap gap-2">
                                        <Link to="/pricing" className="px-5 py-2 rounded-full bg-lq-purple-500 text-white font-semibold hover:bg-lq-purple-700" data-testid="quiz-upgrade-after">
                                            Get unlimited →
                                        </Link>
                                        <button onClick={() => navigate(`/countries/${slug}`)} className="px-5 py-2 rounded-full bg-white border border-lq-ink-300 text-lq-ink-900 hover:bg-lq-ink-100">
                                            <RotateCcw size={14} className="inline mr-1"/> Back
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </article>
            )}
        </main>
    );
}


function MultipleChoiceOptions({ question, answer, setAnswer, result, state }) {
    // For auto-MC (typed-source) questions, the server returns
    // `correct_answer.accepted: [...]` instead of an index. We match by label
    // (case-insensitive) to identify which option is correct.
    const acceptedLower = new Set(
        ((result?.correct_answer?.accepted || []).map((a) => String(a).trim().toLowerCase()))
    );
    const correctIdxFromAccepted = (label) => {
        if (!result) return false;
        if (acceptedLower.size === 0) return false;
        return acceptedLower.has(String(label || '').trim().toLowerCase());
    };

    return (
        <div className="mt-6 grid gap-3" role="radiogroup" data-testid="quiz-options">
            {(question.options || []).map((o, i) => {
                const picked = answer.idx === i;
                const isCorrect = (result?.correct_answer?.index === i) || correctIdxFromAccepted(o.label);
                const wrongPick = result && picked && !result.is_correct;

                // Base styling
                let cls = 'bg-white border-lq-ink-100 hover:border-lq-purple-300 hover:bg-lq-purple-50';
                let icon = null;
                let extra = '';
                if (state === 'reveal') {
                    if (isCorrect) {
                        cls = 'bg-lq-success/10 border-lq-success text-lq-success';
                        icon = <Check size={20} className="text-lq-success" aria-hidden />;
                        extra = picked ? 'pulse-success' : '';
                    } else if (wrongPick) {
                        cls = 'bg-lq-error/10 border-lq-error text-lq-error';
                        icon = <X size={20} className="text-lq-error" aria-hidden />;
                        extra = 'shake';
                    } else {
                        cls = 'bg-white border-lq-ink-100 opacity-60';
                    }
                } else if (picked) {
                    cls = 'bg-lq-purple-100 border-lq-purple-500 ring-2 ring-lq-purple-300';
                }

                return (
                    <button
                        key={i}
                        type="button"
                        role="radio"
                        aria-checked={picked}
                        disabled={state === 'reveal'}
                        onClick={() => setAnswer({ idx: i, typed: '' })}
                        className={`w-full text-left px-4 py-4 rounded-xl border-2 transition-all duration-200 flex items-center gap-3 ${cls} ${extra}`}
                        data-testid={`quiz-option-${i}`}
                    >
                        <span
                            className={`inline-flex items-center justify-center w-9 h-9 rounded-lg font-display font-semibold text-base shrink-0 ${
                                state === 'reveal'
                                    ? (isCorrect ? 'bg-lq-success text-white' : (wrongPick ? 'bg-lq-error text-white' : 'bg-lq-ink-100 text-lq-ink-500'))
                                    : (picked ? 'bg-lq-purple-500 text-white' : 'bg-lq-purple-50 text-lq-purple-700')
                            }`}
                            aria-hidden
                        >
                            {String.fromCharCode(65 + i)}
                        </span>
                        <span className="flex-1 text-base text-lq-ink-900">{o.label}</span>
                        {icon && <span className="ml-auto">{icon}</span>}
                    </button>
                );
            })}
        </div>
    );
}


/**
 * Typed-answer field — fill-in-the-blank style.
 *
 * While the child types, we colour each character green if a prefix of any
 * accepted answer matches what they've typed so far (case-insensitive),
 * otherwise red. This gives gentle real-time guidance without revealing
 * the answer. After submit, the reveal card shows the accepted list.
 */
function TypedAnswerField({ question, answer, setAnswer, result, state, onEnter }) {
    // Find revealed accepted answers (only available on reveal).
    const accepted = (result?.correct_answer?.accepted || []);

    return (
        <div className="mt-6">
            <label className="block">
                <span className="text-sm font-medium text-lq-ink-900">Type your answer</span>
                <input
                    type="text"
                    value={answer.typed}
                    onChange={(e) => setAnswer({ idx: null, typed: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter' && state === 'playing') { e.preventDefault(); onEnter && onEnter(); } }}
                    disabled={state === 'reveal'}
                    maxLength={200}
                    className={`mt-1 w-full rounded-lg border-2 px-3 py-3 text-lg font-mono transition-colors disabled:opacity-70 ${
                        state === 'reveal'
                            ? (result?.is_correct
                                ? 'border-lq-success bg-lq-success/5 text-lq-success'
                                : 'border-lq-error bg-lq-error/5 text-lq-error shake')
                            : 'border-lq-ink-300 focus:border-lq-purple-500'
                    }`}
                    placeholder="Type here…"
                    autoFocus
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    data-testid="quiz-typed-input"
                />
            </label>
            {state === 'reveal' && result && (
                <div className="mt-3 flex items-start gap-2 text-sm" data-testid="quiz-typed-result">
                    {result.is_correct ? (
                        <>
                            <Check size={18} className="text-lq-success mt-0.5" />
                            <span className="text-lq-success font-semibold">Correct!</span>
                        </>
                    ) : (
                        <>
                            <X size={18} className="text-lq-error mt-0.5" />
                            <div>
                                <span className="text-lq-error font-semibold">Not quite.</span>{' '}
                                <span className="text-lq-ink-700">Accepted answers: </span>
                                <span className="font-mono text-lq-ink-900">{accepted.join(' · ')}</span>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
